/**
 * API Endpoints
 * Centralized endpoint definitions for all API calls
 * Note: baseURL in client.ts already includes '/api', so paths here are relative to that
 */

export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },
  USERS: {
    BASE: '/users',
    BY_ID: (id: number) => `/users/${id}`,
    IMPORT_ORDER_SELLERS: '/users/import-order-sellers',
    MENTIONABLE: '/users/mentionable',
    CHANGE_PASSWORD: (id: number) => `/users/${id}/change-password`,
    RESET_PASSWORD: (id: number) => `/users/${id}/reset-password`,
    UPDATE_PROFILE: (id: number) => `/users/${id}/profile`,
  },
  PROFILES: {
    BASE: '/profiles',
    BY_ID: (id: number) => `/profiles/${id}`,
    WITH_STAGES: (id: number) => `/profiles/${id}/with-stages`,
  },
  CATEGORIES: {
    BASE: '/categories',
    BY_ID: (id: number) => `/categories/${id}`,
  },
  COMPLEXITIES: {
    BASE: '/complexities',
    BY_ID: (id: number) => `/complexities/${id}`,
  },
  KANBAN_STAGES: {
    BASE: '/kanban-stages',
    BY_ID: (id: number) => `/kanban-stages/${id}`,
    INITIAL: '/kanban-stages/initial',
    FINAL: '/kanban-stages/final',
    REORDER: '/kanban-stages/reorder',
  },
  ORDERS: {
    BASE: '/orders',
    BY_ID: (id: number) => `/orders/${id}`,
    BY_NUMBER: (orderNumber: string) => `/orders/number/${encodeURIComponent(orderNumber)}`,
    IMPORT: '/orders/import',
    IMPORT_FILE: '/orders/import/file',
    IMPORT_XML_FILE: '/orders/import-xml-file',
    STATISTICS: '/orders/statistics',
    UNBLOCK: (id: number) => `/orders/${id}/unblock`,
    FINANCIAL: (id: number) => `/orders/${id}/financial`,
  },
  PACKAGES: {
    BASE: '/packages',
    BY_ID: (id: number) => `/packages/${id}`,
    BY_STAGE: (stageId: number) => `/packages/stage/${stageId}`,
    BY_ORDER: (orderId: number) => `/packages/by-order/${orderId}`,
    DELAYED: '/packages/delayed',
    SPLIT: '/packages/split',
    DISBURSE: '/packages/disburse',
    COMPLETE: (id: number) => `/packages/${id}/complete`,
    CANCEL: (id: number) => `/packages/${id}/cancel`,
    STATISTICS: '/packages/statistics',
    DASHBOARD_SUMMARY: '/packages/dashboard-summary',
    UPDATE_FORECAST_DATE: (id: number) => `/packages/${id}/forecast-date`,
    UPDATE_ORDEM_CPR: (id: number) => `/packages/${id}/ordem-cpr`,
    UPDATE_CATEGORY: (id: number) => `/packages/${id}/category`,
    UPDATE_PRIORITY: (id: number) => `/packages/${id}/priority`,
    REVERT_SPLIT: (packageId: number, parentOrderItemId: string) =>
      `/packages/${packageId}/split/${parentOrderItemId}`,
    UPDATE_PACKAGE_CODE: (id: number) => `/packages/${id}/package-code`,
  },
  PUBLICATIONS: {
    BASE: '/publications',
    BY_ID: (id: number) => `/publications/${id}`,
    BY_ORDER: (orderId: number) => `/publications/order/${orderId}`,
    BY_PACKAGE: (packageId: number) => `/publications/package/${packageId}`,
    ATTACHMENT_DOWNLOAD: (attachmentId: number) => `/publications/attachments/${attachmentId}/download`,
  },
  PACKAGE_TIME_ENTRIES: {
    BASE: (packageId: number) => `/packages/${packageId}/time-entries`,
    ACTIVE: (packageId: number) => `/packages/${packageId}/time-entries/active`,
    ACTIVE_ALL: '/package-time-entries/active',
    START: (packageId: number) => `/packages/${packageId}/time-entries/start`,
    MANUAL: (packageId: number) => `/packages/${packageId}/time-entries/manual`,
    PAUSE: (packageId: number, entryId: number) => `/packages/${packageId}/time-entries/${entryId}/pause`,
    RESUME: (packageId: number, entryId: number) => `/packages/${packageId}/time-entries/${entryId}/resume`,
    FINALIZE: (packageId: number, entryId: number) => `/packages/${packageId}/time-entries/${entryId}/finalize`,
    UPDATE: (packageId: number, entryId: number) => `/packages/${packageId}/time-entries/${entryId}`,
    DELETE: (packageId: number, entryId: number) => `/packages/${packageId}/time-entries/${entryId}`,
    SUMMARY: (packageId: number) => `/packages/${packageId}/time-entries/summary`,
  },
  REPORTS: {
    COMPLETED_PACKAGES: '/reports/completed-packages',
    ORDER_PROGRESS: '/reports/order-progress',
    BILLINGS: '/reports/billings',
    TIME_ENTRIES: '/reports/time-entries',
  },
  MERCURIO: {
    SYNC: '/mercurio/sync',
    SYNC_STATUS: '/mercurio/sync/status',
  },
  BILLINGS: {
    IMPORT_XML: '/billings/import-xml',
    BY_ORDER_ID: (orderId: number) => `/billings/by-order/${orderId}`,
    BY_ORDER_NUMBER: (orderNumber: string) => `/billings/by-order-number/${orderNumber}`,
    DASHBOARD_SUMMARY: '/billings/dashboard-summary',
    HISTORICAL: '/billings/historical',
    HISTORICAL_BY_ID: (id: number) => `/billings/historical/${id}`,
    HISTORICAL_IMPORT: '/billings/historical/import',
  },
  HISTORICAL_SALES: {
    BASE: '/historical-sales',
    BY_ID: (id: number) => `/historical-sales/${id}`,
    IMPORT: '/historical-sales/import',
  },
  ADMIN: {
    ORDER_IMPORT_EMAIL_RECIPIENTS: '/order-import-email-recipients',
    ORDER_IMPORT_EMAIL_RECIPIENT_BY_ID: (id: number) => `/order-import-email-recipients/${id}`,
  },
  WORKS_CALENDAR: {
    ALL: '/works-calendar',
    BY_PACKAGE: (packageId: number) => `/works-calendar/packages/${packageId}`,
    BY_ID: (id: number) => `/works-calendar/${id}`,
    STAGES_CONFIG: '/works-calendar/stages-config',
    PEOPLE: '/works-calendar/people',
    PEOPLE_BY_ID: (id: number) => `/works-calendar/people/${id}`,
    EQUIPMENT: '/works-calendar/equipment',
    EQUIPMENT_BY_ID: (id: number) => `/works-calendar/equipment/${id}`,
  },
  DASHBOARDS: {
    MINE: '/dashboards/mine',
    BASE: '/dashboards',
    BY_ID: (id: number) => `/dashboards/${id}`,
    BY_PROFILE: (profileId: number) => `/dashboards/profile/${profileId}`,
    ENSURE_BY_PROFILE: (profileId: number) => `/dashboards/profile/${profileId}/ensure`,
    COPY_FROM: (targetProfileId: number, sourceProfileId: number) =>
      `/dashboards/profile/${targetProfileId}/copy-from/${sourceProfileId}`,
    TABS: (dashboardId: number) => `/dashboards/${dashboardId}/tabs`,
    TAB_BY_ID: (tabId: number) => `/dashboards/tabs/${tabId}`,
    REORDER_TABS: (dashboardId: number) => `/dashboards/${dashboardId}/tabs/reorder`,
    COMPONENTS: (tabId: number) => `/dashboards/tabs/${tabId}/components`,
    COMPONENT_BY_ID: (componentId: number) => `/dashboards/components/${componentId}`,
    LAYOUT: (tabId: number) => `/dashboards/tabs/${tabId}/layout`,
  },
} as const;
